import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages } from '@/lib/supabasePages';
import type { ItemComment, LibraryComments } from './LibraryContext';

interface CommentRow {
  id: string;
  item_id: string;
  body: string;
  created_at: string;
}

const COLUMNS = 'id, item_id, body, created_at';

function toComment(row: CommentRow): ItemComment {
  return { id: row.id, itemId: row.item_id, body: row.body, createdAt: row.created_at };
}

/**
 * Comments on items, backed by public.item_comments (RLS: the item's owner
 * only). Counts load once for the tile badges; a thread loads when it opens.
 */
export function useSupabaseComments(): LibraryComments {
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    // Every row, past the API's 1000-row cap
    fetchAllPages(
      (from, to, withCount) =>
        supabase
          .from('item_comments')
          .select('id, item_id', withCount ? { count: 'exact' } : undefined)
          .order('id')
          .range(from, to),
      (row) => row.id,
    ).then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.warn('[Comments] Could not load counts:', error.message);
        return;
      }
      const next: Record<string, number> = {};
      for (const row of data ?? []) next[row.item_id] = (next[row.item_id] ?? 0) + 1;
      setCounts(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const bump = useCallback((itemId: string, by: number) => {
    setCounts((prev) => {
      const n = Math.max(0, (prev[itemId] ?? 0) + by);
      const next = { ...prev };
      if (n) next[itemId] = n;
      else delete next[itemId];
      return next;
    });
  }, []);

  const list = useCallback(async (itemId: string) => {
    const { data, error } = await supabase.from('item_comments').select(COLUMNS).eq('item_id', itemId).order('created_at', { ascending: true });
    if (error) {
      console.warn('[Comments] Could not load:', error.message);
      return [];
    }
    return (data ?? []).map(toComment);
  }, []);

  const add = useCallback(
    async (itemId: string, body: string) => {
      const text = body.trim();
      if (!text) return null;
      const { data, error } = await supabase.from('item_comments').insert({ item_id: itemId, body: text }).select(COLUMNS).single();
      if (error || !data) {
        toast.error('Could not save your comment');
        return null;
      }
      bump(itemId, 1);
      return toComment(data);
    },
    [bump],
  );

  const remove = useCallback(
    async (comment: ItemComment) => {
      const { error } = await supabase.from('item_comments').delete().eq('id', comment.id);
      if (error) {
        toast.error('Could not delete that comment');
        return false;
      }
      bump(comment.itemId, -1);
      return true;
    },
    [bump],
  );

  return useMemo(() => ({ counts, list, add, remove }), [counts, list, add, remove]);
}
